import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, MapPin, Building2 } from "lucide-react";
import { BRAND, NAV_LINKS, BRANCHES, COMPANY } from "../lib/bakeryData";

export const Footer = () => {
    return (
        <footer
            data-testid="site-footer"
            className="bg-[#2A1A17] text-[#F3EBE1] mt-24"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
                <div className="grid md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <img
                            src={BRAND.logo}
                            alt="The Baker's Basket"
                            className="h-20 md:h-24 w-auto object-contain mb-5 -ml-2"
                        />
                        <p className="font-handwriting text-2xl text-[#C5832B] mb-5">
                            {BRAND.tagline}
                        </p>
                        <p className="text-sm text-[#F3EBE1]/70 max-w-sm leading-relaxed">
                            A small bakery with three big homes — Ambikapur, Surajpur, and
                            Bishrampur. Baked fresh each morning since {BRAND.foundedOn}.
                        </p>
                        <div className="flex items-center gap-3 mt-7">
                            <a
                                href={COMPANY.instagram.url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="footer-instagram"
                                aria-label={`Instagram @${COMPANY.instagram.handle}`}
                                className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-[#F3EBE1]/20 hover:bg-[#C5832B] hover:border-[#C5832B] hover:text-[#2A1A17] transition-colors text-xs"
                            >
                                <Instagram className="w-4 h-4" />
                                @{COMPANY.instagram.handle}
                            </a>
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <h4 className="font-serif text-xl mb-5 text-[#F3EBE1]">Explore</h4>
                        <ul className="space-y-3">
                            {NAV_LINKS.map((l) => (
                                <li key={l.to}>
                                    <Link
                                        to={l.to}
                                        data-testid={`footer-link-${l.label.toLowerCase()}`}
                                        className="text-sm text-[#F3EBE1]/75 hover:text-[#C5832B] transition-colors"
                                    >
                                        {l.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <h4 className="font-serif text-xl mb-5 text-[#F3EBE1]">Find Us</h4>
                        <ul className="space-y-4">
                            {BRANCHES.map((b) => (
                                <li key={b.id} className="text-sm text-[#F3EBE1]/75">
                                    <div className="flex items-start gap-2.5">
                                        <MapPin className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <span className="text-[#F3EBE1]">{b.name}</span>
                                            <span className="block text-xs text-[#F3EBE1]/55 mt-0.5 leading-relaxed">
                                                {b.address}
                                            </span>
                                            <a
                                                href={`tel:${b.phone.replace(/[^\d+]/g, "")}`}
                                                className="inline-flex items-center gap-1.5 text-xs text-[#C5832B] hover:text-[#F3EBE1] transition-colors mt-1.5"
                                            >
                                                <Phone className="w-3 h-3" />
                                                {b.phone}
                                            </a>
                                        </div>
                                    </div>
                                </li>
                            ))}
                            <li className="text-sm text-[#F3EBE1]/75 flex items-start gap-2.5 pt-3 border-t border-[#F3EBE1]/10 mt-2">
                                <Building2 className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                <div>
                                    <span className="text-[10px] text-[#F3EBE1]/40 uppercase tracking-wide block">
                                        Corporate office
                                    </span>
                                    <span className="block text-xs text-[#F3EBE1]/70 mt-1 leading-relaxed">
                                        {COMPANY.corporateOffice}
                                    </span>
                                </div>
                            </li>
                            <li className="text-sm text-[#F3EBE1]/75 flex items-center gap-2.5 pt-3 border-t border-[#F3EBE1]/10 mt-2">
                                <Phone className="w-4 h-4 text-[#C5832B]" />
                                <a href={`tel:${COMPANY.customerCare.phone.replace(/[^\d+]/g, "")}`} className="hover:text-[#C5832B] transition-colors">
                                    {COMPANY.customerCare.phone}
                                </a>
                                <span className="text-[10px] text-[#F3EBE1]/40 uppercase tracking-wide">
                                    Customer care
                                </span>
                            </li>
                            <li className="text-sm text-[#F3EBE1]/75 flex items-center gap-2.5">
                                <Mail className="w-4 h-4 text-[#C5832B]" />
                                <a href={`mailto:${COMPANY.customerCare.email}`} className="hover:text-[#C5832B] transition-colors">
                                    {COMPANY.customerCare.email}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-[#F3EBE1]/10 flex flex-col md:flex-row items-center justify-between gap-3">
                    <p className="text-xs text-[#F3EBE1]/55">
                        © {new Date().getFullYear()} {COMPANY.firmName} · GSTIN {COMPANY.gstin}
                    </p>
                    <p className="text-xs text-[#F3EBE1]/55 font-handwriting text-base">
                        Hand-kneaded in Chhattisgarh.
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
